import { PropsWithChildren, useEffect } from 'react';
import type { i18n } from 'i18next';
import { I18nextProvider } from 'react-i18next';
import { I18nProvider } from 'react-aria-components';
import { initializeMoment } from 'v5/i18n.moment';
import { useRouteMatchesPublicPaths } from 'v5/i18n.publicPaths';
import { internalInstance } from './i18n.internal';
import { publicInstance } from './i18n.public';

/** Translations and the date and number formats of one i18next instance. */
export function I18nInstanceProvider({
  i18n,
  children
}: PropsWithChildren<{ i18n: i18n }>) {
  return (
    <I18nextProvider i18n={i18n}>
      <I18nProvider locale={i18n.language ?? 'en'}>{children}</I18nProvider>
    </I18nextProvider>
  );
}

export function PublicLocaleProvider(props: PropsWithChildren) {
  return <I18nInstanceProvider i18n={publicInstance} {...props} />;
}

export function InternalLocaleProvider(props: PropsWithChildren) {
  return <I18nInstanceProvider i18n={internalInstance} {...props} />;
}

/**
 * moment is a global singleton, so we need to set the locale globally, just before rendering it in current language
 */
export function MomentLocaleGlobalConfigurator() {
  const matchesPublic = useRouteMatchesPublicPaths();

  useEffect(() => {
    const currentInstance = matchesPublic ? publicInstance : internalInstance;
    return initializeMoment(currentInstance, currentInstance.language);
  }, [matchesPublic]);
  return null;
}
